import { type DBSchema, deleteDB, type IDBPDatabase, openDB } from "idb";
import type { StoredRecord } from "./object-pool";
import type { Transaction } from "./types";

type Meta = { schemaVersion?: number; cursor?: number; partials?: string[] };

interface GridcastDB extends DBSchema {
	records: { key: [string, string]; value: StoredRecord; indexes: { model: string } };
	transactions: { key: string; value: Transaction };
	meta: { key: string; value: unknown };
}

type Op =
	| { kind: "put"; record: StoredRecord }
	| { kind: "remove"; model: string; id: string }
	| { kind: "putTx"; tx: Transaction }
	| { kind: "deleteTx"; id: string }
	| { kind: "meta"; key: keyof Meta; value: unknown };

const PREFIX = "gridcast";
const dbName = (userId: string, workspaceId: string) => `${PREFIX}:${userId}:${workspaceId}`;

/**
 * One IndexedDB database per (user, workspace). Writes are queued and committed in a
 * single IDB transaction per microtask, so a 200-change delta costs one commit.
 */
export class LocalDb {
	private queue: Op[] = [];
	private flushing: Promise<void> | null = null;

	private constructor(private readonly db: IDBPDatabase<GridcastDB>) {}

	static async open(userId: string, workspaceId: string) {
		const db = await openDB<GridcastDB>(dbName(userId, workspaceId), 1, {
			upgrade(database) {
				const records = database.createObjectStore("records", { keyPath: ["model", "id"] });
				records.createIndex("model", "model");
				database.createObjectStore("transactions", { keyPath: "id" });
				database.createObjectStore("meta");
			},
		});
		return new LocalDb(db);
	}

	async load() {
		const tx = this.db.transaction(["records", "transactions", "meta"], "readonly");
		const [records, transactions, schemaVersion, cursor, partials] = await Promise.all([
			tx.objectStore("records").getAll(),
			tx.objectStore("transactions").getAll(),
			tx.objectStore("meta").get("schemaVersion"),
			tx.objectStore("meta").get("cursor"),
			tx.objectStore("meta").get("partials"),
		]);
		const meta: Meta = {
			schemaVersion: schemaVersion as number | undefined,
			cursor: cursor as number | undefined,
			partials: partials as string[] | undefined,
		};
		return { records, transactions: transactions.sort((a, b) => a.createdAt - b.createdAt), meta };
	}

	put(record: StoredRecord) {
		this.enqueue({ kind: "put", record });
	}

	remove(model: string, id: string) {
		this.enqueue({ kind: "remove", model, id });
	}

	putTransaction(tx: Transaction) {
		this.enqueue({ kind: "putTx", tx });
	}

	deleteTransaction(id: string) {
		this.enqueue({ kind: "deleteTx", id });
	}

	setMeta<K extends keyof Meta>(key: K, value: Meta[K]) {
		this.enqueue({ kind: "meta", key, value });
	}

	async clear() {
		this.queue = [];
		const tx = this.db.transaction(["records", "transactions", "meta"], "readwrite");
		await Promise.all([
			tx.objectStore("records").clear(),
			tx.objectStore("transactions").clear(),
			tx.objectStore("meta").clear(),
			tx.done,
		]);
	}

	async close() {
		await this.flush();
		this.db.close();
	}

	flush(): Promise<void> {
		if (this.flushing) return this.flushing.then(() => this.flush());
		if (!this.queue.length) return Promise.resolve();
		const ops = this.queue;
		this.queue = [];
		const tx = this.db.transaction(["records", "transactions", "meta"], "readwrite");
		const records = tx.objectStore("records");
		const transactions = tx.objectStore("transactions");
		const meta = tx.objectStore("meta");
		for (const op of ops) {
			if (op.kind === "put") records.put(op.record);
			else if (op.kind === "remove") records.delete([op.model, op.id]);
			else if (op.kind === "putTx") transactions.put(op.tx);
			else if (op.kind === "deleteTx") transactions.delete(op.id);
			else meta.put(op.value, op.key);
		}
		this.flushing = tx.done
			.catch((error) => console.error("IndexedDB write failed", error))
			.finally(() => {
				this.flushing = null;
			});
		return this.flushing;
	}

	private enqueue(op: Op) {
		this.queue.push(op);
		if (this.queue.length === 1) queueMicrotask(() => void this.flush());
	}
}

/** Called on explicit logout so a shared machine keeps no workspace data. */
export async function deleteLocalData(userId: string) {
	const databases = (await indexedDB.databases?.()) ?? [];
	await Promise.all(
		databases
			.map((db) => db.name)
			.filter((name): name is string => !!name?.startsWith(`${PREFIX}:${userId}:`))
			.map((name) => deleteDB(name)),
	);
}
